import React, { createContext, useContext, useState, useEffect } from 'react';
import { ClientAssignment } from '../features/cases/types';

// Mock user database - Updated to match exact ClientAssignment enum values
const MOCK_USERS = {
  'craig.admin': {
    password: 'admin123',
    role: 'admin',
    clientType: ClientAssignment.CRAIG, // 'craig'
    displayName: 'Craig (Admin)',
    hasFullAccess: true
  },
  'johnson.law': {
    password: 'legal456',
    role: 'client',
    clientType: ClientAssignment.JOHNSON_LAW, // 'johnson_law'
    displayName: 'Johnson Law',
    hasFullAccess: false
  },
  'smith.insurance': {
    password: 'insure789',
    role: 'client',
    clientType: ClientAssignment.SMITH_INSURANCE, // 'smith_insurance'
    displayName: 'Smith Insurance',
    hasFullAccess: false
  },
  'martinez.legal': {
    password: 'lawyer321',
    role: 'client',
    clientType: ClientAssignment.MARTINEZ_LEGAL, // 'martinez_legal'
    displayName: 'Martinez Legal',
    hasFullAccess: false
  },
  'brown.associates': {
    password: 'assoc654',
    role: 'client',
    clientType: ClientAssignment.BROWN_ASSOCIATES, // 'brown_associates'
    displayName: 'Brown & Associates',
    hasFullAccess: false
  }
} as const;

interface User {
  username: string;
  role: 'admin' | 'client';
  clientType: ClientAssignment;
  displayName: string;
  hasFullAccess: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  canViewCase: (caseCollectingFor: ClientAssignment) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('invoice_tracker_user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('invoice_tracker_user');
      }
    }
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const mockUser = MOCK_USERS[username as keyof typeof MOCK_USERS];
    
    if (mockUser && mockUser.password === password) {
      const userObj: User = {
        username,
        role: mockUser.role,
        clientType: mockUser.clientType,
        displayName: mockUser.displayName,
        hasFullAccess: mockUser.hasFullAccess
      };
      
      setUser(userObj);
      localStorage.setItem('invoice_tracker_user', JSON.stringify(userObj));
      return true;
    }
    
    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('invoice_tracker_user');
  };

  const canViewCase = (caseCollectingFor: ClientAssignment): boolean => {
    if (!user) {
      console.log('canViewCase: No user logged in');
      return false;
    }
    
    // Add debugging
    console.log('canViewCase check:', {
      userClientType: user.clientType,
      caseCollectingFor: caseCollectingFor,
      userHasFullAccess: user.hasFullAccess,
      userDisplayName: user.displayName
    });
    
    // Admin can view all cases
    if (user.hasFullAccess) {
      console.log('canViewCase: Admin access - returning true');
      return true;
    }
    
    // Clients can only view cases assigned to them
    const canView = user.clientType === caseCollectingFor;
    console.log('canViewCase: Client access result:', canView);
    return canView;
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    canViewCase
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 